import { Group, Mesh, MeshStandardMaterial, PlaneGeometry } from 'three';
import { CROSS_ROAD_HALF, STOP_LINE_OFFSET } from '../../content/junctions';
import { flatMaterial } from '../../render/materials/cache';
import { canvasTexture, repeating } from '../../render/materials/canvasTexture';
import { LAYOUT } from './roadLayout';
import { TrafficLights } from './trafficLights';

/** Junctions sit in the middle of a tile: local z of the crossing road's centre. */
export const JUNCTION_LOCAL_Z = -LAYOUT.tileLength / 2;
/** The crossing road cuts the kerbs and pavements over this local z range (positive numbers, along the tile). */
export const JUNCTION_GAP = { from: LAYOUT.tileLength / 2 - CROSS_ROAD_HALF - 0.5, to: LAYOUT.tileLength / 2 + CROSS_ROAD_HALF + 0.5 } as const;

const CROSS_LENGTH = 220;
const CROSS_CENTRE_X = -40;

let crossMaterial: MeshStandardMaterial | null = null;
let zebraMaterial: MeshStandardMaterial | null = null;

function crossRoadMaterial(): MeshStandardMaterial {
  crossMaterial ??= new MeshStandardMaterial({
    map: repeating(
      canvasTexture(256, 128, (ctx) => {
        ctx.fillStyle = '#3d3d41';
        ctx.fillRect(0, 0, 256, 128);
        ctx.fillStyle = '#e9e4d8';
        ctx.fillRect(0, 62, 150, 4);
        ctx.fillRect(0, 4, 256, 3);
        ctx.fillRect(0, 121, 256, 3);
      }),
      CROSS_LENGTH / 20,
      1,
    ),
  });
  return crossMaterial;
}

function zebra(): MeshStandardMaterial {
  zebraMaterial ??= new MeshStandardMaterial({
    transparent: true,
    map: repeating(
      canvasTexture(64, 32, (ctx) => {
        ctx.clearRect(0, 0, 64, 32);
        ctx.fillStyle = '#f2efe6';
        ctx.fillRect(0, 0, 36, 32);
      }),
      10,
      1,
    ),
  });
  return zebraMaterial;
}

function flat(width: number, depth: number, material: MeshStandardMaterial, x: number, z: number, y = 0.012): Mesh {
  const mesh = new Mesh(new PlaneGeometry(width, depth), material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(x, y, z);
  mesh.receiveShadow = true;
  return mesh;
}

/** Crossing road, zebra crossings, stop lines and traffic lights for a junction tile. */
export function buildJunction(): { group: Group; lights: TrafficLights } {
  const group = new Group();
  const zc = JUNCTION_LOCAL_Z;
  const near = zc + CROSS_ROAD_HALF;
  const far = zc - CROSS_ROAD_HALF;
  const stopZ = zc + STOP_LINE_OFFSET;
  const roadWidth = LAYOUT.roadHalf * 2;
  group.add(
    flat(CROSS_LENGTH, CROSS_ROAD_HALF * 2, crossRoadMaterial(), CROSS_CENTRE_X, zc, 0.006),
    flat(roadWidth, 1.6, zebra(), 0, near + 0.9),
    flat(LAYOUT.oppositeRoadWidth, 1.6, zebra(), LAYOUT.oppositeRoadX, far - 0.9),
    flat(roadWidth, 0.35, flatMaterial('#f2efe6'), 0, stopZ),
    flat(LAYOUT.oppositeRoadWidth, 0.35, flatMaterial('#f2efe6'), LAYOUT.oppositeRoadX, zc - STOP_LINE_OFFSET),
  );
  const lights = new TrafficLights({
    ours: [
      { x: LAYOUT.kerbX + 0.8, z: stopZ },
      { x: LAYOUT.medianX, z: stopZ },
    ],
    cross: [
      { x: LAYOUT.kerbX + 1.5, z: far - 0.8, rotY: Math.PI / 2 },
      { x: LAYOUT.leftSidewalkX + 1.5, z: near + 0.8, rotY: -Math.PI / 2 },
    ],
  });
  group.add(lights.group);
  return { group, lights };
}
